/**
 * Currency formatting utilities
 * 
 * Centralized currency formatting for consistent display across the application.
 * Defaults to Thai Baht (THB) with th-TH locale formatting.
 */

/**
 * Format a number as Thai Baht (THB) currency
 * 
 * @param value - The numeric value to format
 * @returns Formatted currency string (e.g., "฿1,234.56")
 * 
 * @example
 * formatCurrencyTHB(1234.56) // "฿1,234.56"
 * formatCurrencyTHB(0) // "฿0.00"
 */
export function formatCurrencyTHB(value: number): string {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a number as currency with custom locale and currency
 * 
 * @param value - The numeric value to format
 * @param locale - Locale string (e.g., "en-US", "th-TH")
 * @param currency - Currency code (e.g., "USD", "THB", "EUR")
 * @returns Formatted currency string
 * 
 * @example
 * formatCurrency(1234.56, "en-US", "USD") // "$1,234.56"
 * formatCurrency(1234.56, "th-TH", "THB") // "฿1,234.56"
 */
export function formatCurrency(
  value: number,
  locale: string = "th-TH",
  currency: string = "THB"
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Map a currency code to its default locale for Intl formatting.
 */
export function currencyLocale(currency: string): string {
  if (currency === "THB") return "th-TH";
  return "en-US";
}

/**
 * Format a price with automatic locale resolution.
 * Returns an em-dash for null/undefined values.
 *
 * @example
 * formatPrice(299, "THB")   // "฿299.00"
 * formatPrice(null, "USD")  // "—"
 */
export function formatPrice(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined) return "\u2014";
  return formatCurrency(value, currencyLocale(currency), currency);
}

